/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { CacheType, createObservableDataAction, IAction, IActionContext, IActionInput, IAny, ICreateActionContext, IGeneric } from '@msdyn365-commerce/core';
import { ProductAvailableQuantity, ProductsDataActions, ProductType,
    ProductWarehouseInventoryAvailability, ReleasedProductType } from '@msdyn365-commerce/retail-proxy';

import getSelectedVariant, { SelectedVariantInput } from './get-selected-variant';
import { ArrayExtensions, getSelectedProductIdFromActionInput } from './utilities';
import { DeliveryMode, IProductInventoryInformation } from './utilities/product-inventory-information';
import { createInventoryAvailabilitySearchCriteria, mapAggregatedProductInventoryInformation } from './utilities/product-inventory-utils';

/**
 * Input class for the getProductAvailabilitiesForSelectedVariant Data Action.
 */
export class ProductAvailabilitiesForSelectedVariantInput implements IActionInput {
    public productId: number;

    public channelId: number;

    public constructor(productId: number | string, channelId: number) {
        this.productId = typeof productId === 'string' ? +productId : productId;
        this.channelId = channelId;
    }

    /**
     * GetCacheKey.
     * @returns - Returns string.
     */
    public getCacheKey = (): string => 'ProductAvailabilitiesForSelectedVariant';

    /**
     * GetCacheObjectType.
     * @returns - Cache Object Type string.
     */
    public getCacheObjectType = (): string => 'ProductAvailabilities';

    /**
     * DataCacheType.
     * @returns - CacheType string.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the getProductAvailabilitiesForSelectedVariant data action.
 * @param inputData - The input data passed to the createInput method.
 * @returns - ProductAvailabilitiesForSelectedVariant or error.
 */
export const createProductAvailabilitiesForSelectedVariantInput = (
    inputData: ICreateActionContext<IGeneric<IAny>>
): ProductAvailabilitiesForSelectedVariantInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);

    if (productId) {
        return new ProductAvailabilitiesForSelectedVariantInput(+productId, +inputData.requestContext.apiSettings.channelId);
    }
    throw new Error('Unable to create ProductAvailabilitiesForSelectedVariantInput, no productId found on module config or query');

};

/**
 * The action method for the getProductAvailabilitiesForSelectedVariant data action.
 * @param input - ProductAvailabilitiesForSelectedVariant.
 * @param ctx - Action Context.
 * @returns - Product inventory information.
 */
export async function getProductAvailabilitiesForSelectedVariantAction(
    input: ProductAvailabilitiesForSelectedVariantInput,
    ctx: IActionContext
): Promise<IProductInventoryInformation[] | undefined> {
    const selectedVariantInput = new SelectedVariantInput(input.productId, input.channelId);
    const productInventoryInformation: IProductInventoryInformation[] = [];

    try {

        const productResult = await getSelectedVariant(selectedVariantInput, ctx);

        // Don't get availability if it is service item && enableStockCheck is disbaled.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Reading config.
        if (ctx.requestContext.app.config.enableStockCheck && productResult && productResult.ItemTypeValue !== ReleasedProductType.Service &&
            productResult.ProductTypeValue !== ProductType.Master) {
            const shippingSearchCriteria = createInventoryAvailabilitySearchCriteria(
                ctx,
                [productResult.RecordId],
                true,
                undefined,
                undefined,
                DeliveryMode.shipping);
            const pickupSearchCriteria = createInventoryAvailabilitySearchCriteria(ctx, [input.productId], false, true, undefined, DeliveryMode.pickup);
            const shippingAvailability = await ProductsDataActions.getEstimatedAvailabilityAsync({ callerContext: ctx }, shippingSearchCriteria);
            if (shippingAvailability.ProductWarehouseInventoryAvailabilities) {
                const shippingInventory = mapAggregatedProductInventoryInformation(ctx, shippingAvailability);
                if (ArrayExtensions.hasElements(shippingInventory)) {
                    shippingInventory[0].deliveryType = DeliveryMode.shipping;
                    productInventoryInformation.push(shippingInventory[0]);
                }
            }
            const pickupAvailability = await ProductsDataActions.getEstimatedAvailabilityAsync({ callerContext: ctx }, pickupSearchCriteria);
            if (pickupAvailability.ProductWarehouseInventoryAvailabilities) {
                const pickupInventory = mapAggregatedProductInventoryInformation(ctx, pickupAvailability);
                if (ArrayExtensions.hasElements(pickupInventory)) {
                    pickupInventory[0].deliveryType = DeliveryMode.pickup;
                    productInventoryInformation.push(pickupInventory[0]);
                }
            }
            return productInventoryInformation;
        }
        return undefined;
    } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- cann't assign type to the error variable.
        ctx.telemetry.exception(error);
        ctx.telemetry.debug('[getPriceForSelectedVariantAction]Error executing action');
        return undefined;
    }
}

/**
 * The function that maps a ProductWareHouse object into a ProductAvailabilityQuantity.
 * @param productsWarehouseInventory - Product warehouse inventory.
 * @returns Product Available quantity.
 */
export function mergeProductWarehouseToProductAvailabities(productsWarehouseInventory: ProductWarehouseInventoryAvailability[]): ProductAvailableQuantity[] {
    const productAvailable: ProductAvailableQuantity[] = [];
    if (ArrayExtensions.hasElements(productsWarehouseInventory)) {
        for (const product of productsWarehouseInventory) {
            if (product.TotalAvailable !== undefined && product.ProductId !== undefined) {
                productAvailable.push({ ProductId: product.ProductId, AvailableQuantity: product.TotalAvailable });
            }
        }
    }
    return productAvailable;
}

/**
 * The complete getProductAvailabilitiesForSelectedVariant data action
 * Get the currently selected variant via the getSelectedVariant data action, and
 * then gets the availabilities for the variant via the getProductAvailabilities RetailServer API.
 */
export const getProductAvailabilitiesForSelectedVariantActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-product-availabilities-for-selected-variant',
    action: <IAction<IProductInventoryInformation[] | null>>getProductAvailabilitiesForSelectedVariantAction,
    input: createProductAvailabilitiesForSelectedVariantInput
});

export default getProductAvailabilitiesForSelectedVariantActionDataAction;
